/**
 * @file Shared React component — components/DataVisualisation/StageBarChart.tsx.
 */

import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { CementBoxPlotMetric } from "./CementBoxPlotChart";
import {
  STAGE_BAR_CHART_LEGEND,
  type StageBarChartPoint,
  getStageBarChartAxisLabel,
} from "../../utils/expertFlaggedStageBarChart";
import "./StageBarChart.css";

/** Match CementBoxPlotChart: review queue = black, all/approved pool = salmon. */
const COLORS = {
  individual: "#101010",
  average: "#e8ada8",
  black: "#151515",
};

interface Props {
  metric: CementBoxPlotMetric;
  data: StageBarChartPoint[];
  individualLegendLabel?: string;
  /** When set, keeps the Y-axis fixed so average bars do not rescale on selection. */
  yMax?: number;
}

const StageBarChart: React.FC<Props> = ({ metric, data, individualLegendLabel, yMax: yMaxProp }) => {
  const individualLabel = individualLegendLabel ?? STAGE_BAR_CHART_LEGEND.individual;
  const chartRows = useMemo(
    () =>
      data.map((row) => ({
        stage: row.stage,
        individual: row.individual ?? 0,
        average: row.average ?? 0,
        hasIndividual: row.individual != null,
        hasAverage: row.average != null,
      })),
    [data]
  );

  const yMax = useMemo(() => {
    if (yMaxProp != null && Number.isFinite(yMaxProp) && yMaxProp > 0) {
      return yMaxProp;
    }
    const values = data.flatMap((row) =>
      [row.individual, row.average].filter((v): v is number => v != null)
    );
    if (values.length === 0) return 1;
    const max = Math.max(...values);
    return max <= 0 ? 1 : Math.ceil(max * 1.15 * 100) / 100;
  }, [data, yMaxProp]);

  const hasAnyData = data.some((row) => row.individual != null || row.average != null);

  return (
    <div className="stage-bar-chart">
      <h3 className="stage-bar-chart__title">Review of flagged dataset</h3>

      {!hasAnyData ? (
        <p className="stage-bar-chart__empty" role="status">
          No stage summary data available for the current selection.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={420}>
          <BarChart
            data={chartRows}
            margin={{ top: 16, right: 24, bottom: 56, left: 24 }}
            barCategoryGap="24%"
            barGap={6}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis
              dataKey="stage"
              tick={{ fill: COLORS.black, fontSize: 14, fontWeight: 600 }}
              tickLine={{ stroke: COLORS.black }}
              axisLine={{ stroke: COLORS.black, strokeWidth: 2 }}
              label={{
                value: "Life cycle stage(s)",
                position: "insideBottom",
                offset: -40,
                style: { fill: COLORS.black, fontSize: 13, fontWeight: 600 },
              }}
            />
            <YAxis
              domain={[0, yMax]}
              tick={{ fill: COLORS.black, fontSize: 11 }}
              tickLine={{ stroke: COLORS.black }}
              axisLine={{ stroke: COLORS.black, strokeWidth: 2 }}
              label={{
                value: getStageBarChartAxisLabel(metric),
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle", fontSize: 12, fill: COLORS.black },
              }}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                Number(value).toFixed(3),
                name,
              ]}
            />
            <Bar
              dataKey="individual"
              name={individualLabel}
              fill={COLORS.individual}
              radius={[2, 2, 0, 0]}
            >
              {chartRows.map((row) => (
                <Cell
                  key={`individual-${row.stage}`}
                  fill={row.hasIndividual ? COLORS.individual : "transparent"}
                />
              ))}
            </Bar>
            <Bar
              dataKey="average"
              name={STAGE_BAR_CHART_LEGEND.average}
              fill={COLORS.average}
              radius={[2, 2, 0, 0]}
            >
              {chartRows.map((row) => (
                <Cell
                  key={`average-${row.stage}`}
                  fill={row.hasAverage ? COLORS.average : "transparent"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}

      {hasAnyData ? (
        <div className="stage-bar-chart__legend" aria-label="Stage bar chart legend">
          <span className="stage-bar-chart__legend-item">
            <span
              className="stage-bar-chart__swatch stage-bar-chart__swatch--individual"
              aria-hidden
            />
            {individualLabel}
          </span>
          <span className="stage-bar-chart__legend-item">
            <span
              className="stage-bar-chart__swatch stage-bar-chart__swatch--average"
              aria-hidden
            />
            {STAGE_BAR_CHART_LEGEND.average}
          </span>
        </div>
      ) : null}
    </div>
  );
};

/** React component — Stage Bar Chart. */
export default StageBarChart;
